import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { getMyProfile, updateFullName, updatePhone, uploadProfileImage } from "../../api/userService";
import { getMyStores } from "../../api/storeService";
import { getMySubscriptions, getMySubscriptionDetail } from "../../api/subscriptionService";
import { getStoreCommissionSummary, getStoreFeeRequests, confirmStoreFeeSent } from "../../api/storeFeesService";
import {
  getMyStorePayouts,
  getPendingDeliveryBalances,
  requestStorePayout,
  confirmStorePayoutReceived,
} from "../../api/storePayoutsService";
import {
  SubscriptionStatusLabels,
  SubscriptionStatusColors,
  InvoiceStatusLabels,
  InvoiceStatusColors,
  formatCurrency,
  formatDate,
} from "../Admin/constants";
import { SectionCard, EmptyState } from "../Admin/analyticsShared";
import { brand } from "./theme";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

// Matches the backend's SettlementStatus enum.
const SETTLEMENT_STATUS_GENERATED = 1;
const SETTLEMENT_STATUS_PAID = 2;

// Matches the backend's StorePayoutStatus enum.
const PAYOUT_STATUS_SENT = 1;
const PAYOUT_STATUS_RECEIVED = 2;

export default function Settings() {
  const { t } = useTranslation(["boSettings", "boShared", "boLayout", "common"]);
  const { logout, updateUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [hasPrimeStore, setHasPrimeStore] = useState(false);
  const [hasStandardStore, setHasStandardStore] = useState(false);
  const [loadingStores, setLoadingStores] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    getMyProfile()
      .then(setProfile)
      .catch(() => setError(t("boSettings:loadProfileError")))
      .finally(() => setLoadingProfile(false));

    getMySubscriptions()
      .then(setSubscriptions)
      .catch(() => setError((prev) => prev || t("boSettings:loadSubscriptionsError")))
      .finally(() => setLoadingSubscriptions(false));

    getMyStores()
      .then((stores) => {
        setHasPrimeStore(stores.some((s) => s.isPrime));
        setHasStandardStore(stores.some((s) => !s.isPrime));
      })
      .catch(() => setError((prev) => prev || t("boSettings:loadStoresError")))
      .finally(() => setLoadingStores(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLogout() {
    logout();
    navigate("/business-owner/login");
  }

  async function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError(t("boShared:invalidImageType"));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError(t("boSettings:photoTooLarge"));
      return;
    }

    setError("");
    setUploadingPhoto(true);
    try {
      const result = await uploadProfileImage(file);
      setProfile((prev) => ({ ...prev, profileImageUrl: result.profileImageUrl }));
      updateUser({ profileImageUrl: result.profileImageUrl });
    } catch {
      setError(t("boSettings:photoUploadFailed"));
    } finally {
      setUploadingPhoto(false);
    }
  }

  function handleSavedFullName(fullName) {
    setProfile((prev) => ({ ...prev, fullName }));
    updateUser({ fullName });
    setNotice(t("boSettings:nameUpdated"));
  }

  function handleSavedPhone(phone) {
    setProfile((prev) => ({ ...prev, phone }));
    setNotice(t("boSettings:phoneUpdated"));
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 3 }}>
        <Avatar
          variant="rounded"
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            background: `linear-gradient(135deg, ${brand.emeraldLight}, ${brand.emeraldDeep})`,
            boxShadow: `0 6px 16px ${alpha(brand.emeraldDeep, 0.35)}`,
          }}
        >
          <SettingsRoundedIcon />
        </Avatar>
        <Box>
          <Typography variant="h4">{t("common:settings")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("boSettings:subtitle")}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Stack spacing={3}>
        <Card sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              height: 88,
              background: `linear-gradient(135deg, ${alpha(brand.emeraldLight, 0.28)}, ${alpha(brand.amber, 0.24)})`,
            }}
          />
          <CardContent sx={{ pt: 0 }}>
            {loadingProfile ? (
              <>
                <Stack direction="row" spacing={2.5} alignItems="flex-end" sx={{ mt: "-40px", mb: 2 }}>
                  <Skeleton variant="circular" width={80} height={80} sx={{ border: "4px solid #fff" }} />
                  <Stack spacing={1} sx={{ flexGrow: 1, pb: 0.5 }}>
                    <Skeleton variant="text" width="40%" height={28} />
                    <Skeleton variant="text" width="60%" />
                  </Stack>
                </Stack>
                <Divider sx={{ mb: 2.5 }} />
                <Skeleton variant="rounded" height={56} sx={{ mb: 1.5 }} />
                <Skeleton variant="rounded" height={56} />
              </>
            ) : (
              <Stack spacing={0}>
                <Stack direction="row" spacing={2.5} alignItems="flex-end" sx={{ mt: "-40px", mb: 2 }}>
                  <Box
                    component="label"
                    sx={{
                      position: "relative",
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      cursor: "pointer",
                      flexShrink: 0,
                      p: 0.5,
                      bgcolor: "background.paper",
                      boxShadow: 3,
                      "&:hover .photo-overlay": { opacity: 1 },
                    }}
                  >
                    <Avatar
                      src={profile?.profileImageUrl || undefined}
                      sx={{ width: "100%", height: "100%", fontSize: 26, bgcolor: "primary.main" }}
                    >
                      {(profile?.fullName || "B").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box
                      className="photo-overlay"
                      sx={{
                        position: "absolute",
                        inset: 4,
                        borderRadius: "50%",
                        bgcolor: "rgba(15, 23, 42, 0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: uploadingPhoto ? 1 : 0,
                        transition: "opacity .15s",
                      }}
                    >
                      {uploadingPhoto ? (
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                      ) : (
                        <CameraAltOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
                      )}
                    </Box>
                    <input type="file" accept="image/*" hidden onChange={handlePhotoChange} disabled={uploadingPhoto} />
                  </Box>

                  <Box sx={{ minWidth: 0, pb: 0.5 }}>
                    <Typography variant="h6" noWrap>
                      {profile?.fullName || t("boLayout:businessOwnerFallback")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {profile?.email}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <EditableField
                      label={t("boSettings:fullName")}
                      value={profile?.fullName || ""}
                      onSave={async (value) => {
                        await updateFullName(value);
                        handleSavedFullName(value);
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <EditableField
                      label={t("boShared:phone")}
                      value={profile?.phone || ""}
                      placeholder={t("boSettings:noPhoneOnFile")}
                      onSave={async (value) => {
                        const result = await updatePhone(value);
                        handleSavedPhone(result.phone);
                      }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            )}
          </CardContent>
        </Card>

        <LanguagePreferenceCard />

        <SectionCard title={t("boSettings:billingTitle")} subtitle={t("boSettings:billingSubtitle")}>
          {loadingSubscriptions ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={72} />
              <Skeleton variant="rounded" height={72} />
            </Stack>
          ) : subscriptions.length === 0 ? (
            <EmptyState text={t("boSettings:noSubscriptions")} icon={CardMembershipRoundedIcon} />
          ) : (
            <Stack spacing={1.5}>
              {subscriptions.map((sub) => (
                <SubscriptionAccordion key={sub.storeId} subscription={sub} />
              ))}
            </Stack>
          )}
        </SectionCard>

        {loadingStores ? (
          <Skeleton variant="rounded" height={140} />
        ) : (
          <>
            {hasPrimeStore && <PayoutsSection />}
            {hasStandardStore && <CommissionSection />}
          </>
        )}

        <Card>
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t("boSettings:sessionTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("boSettings:sessionSubtitle")}
                </Typography>
              </Box>
              <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={handleLogout}>
                {t("common:logOut")}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

function LanguagePreferenceCard() {
  const { t } = useTranslation("boSettings");
  const { language, setLanguage, languages } = useLanguage();

  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar variant="rounded" sx={{ bgcolor: "action.hover", color: "text.secondary", width: 40, height: 40 }}>
              <LanguageRoundedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {t("languageTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("languageSubtitle")}
              </Typography>
            </Box>
          </Stack>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
              {Object.values(languages).map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.nativeLabel}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </CardContent>
    </Card>
  );
}

function EditableField({ label, value, placeholder, onSave }) {
  const { t } = useTranslation("boShared");
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setInput(value);
  }, [value]);

  async function handleSave() {
    const trimmed = input.trim();
    if (!trimmed) {
      setError(t("fieldRequired"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(trimmed);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data || t("saveChangeFailed"));
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setInput(value);
    setError("");
    setEditing(false);
  }

  if (!editing) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}
          >
            {label}
          </Typography>
          <Typography variant="body2" color={value ? "text.primary" : "text.secondary"} noWrap>
            {value || placeholder || "-"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setEditing(true)}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          label={label}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          size="small"
          fullWidth
          autoFocus
          disabled={saving}
          error={Boolean(error)}
          helperText={typeof error === "string" ? error : ""}
        />
        <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}>
          <CheckRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleCancel} disabled={saving}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

function SubscriptionAccordion({ subscription }) {
  const { t } = useTranslation(["boSettings", "boShared"]);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  async function handleChange(_e, expanded) {
    if (expanded && !detail) {
      setLoadingDetail(true);
      setError("");
      try {
        const data = await getMySubscriptionDetail(subscription.storeId);
        setDetail(data);
      } catch {
        setError(t("boSettings:loadInvoiceError"));
      } finally {
        setLoadingDetail(false);
      }
    }
  }

  return (
    <Accordion
      onChange={handleChange}
      disableGutters
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, "&:before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%", pr: 1 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(brand.tealStart, 0.12), color: "primary.dark" }}>
            <CardMembershipRoundedIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {subscription.storeName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatCurrency(subscription.monthlyPrice)} / month · {subscription.effectiveCommissionPercent}% commission
            </Typography>
          </Box>
          <Chip
            size="small"
            label={SubscriptionStatusLabels[subscription.status] || subscription.status}
            color={SubscriptionStatusColors[subscription.status] || "default"}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {t("boSettings:trialEnds")}
            </Typography>
            <Typography variant="body2">{formatDate(subscription.trialEndsAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {t("boSettings:currentPeriodEnd")}
            </Typography>
            <Typography variant="body2">{formatDate(subscription.currentPeriodEnd)}</Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loadingDetail ? (
          <Skeleton variant="rounded" height={100} />
        ) : detail && detail.invoices.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("boSettings:period")}</TableCell>
                <TableCell>{t("boSettings:amount")}</TableCell>
                <TableCell>{t("boSettings:due")}</TableCell>
                <TableCell>{t("boShared:status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.invoices.map((inv) => (
                <TableRow key={inv.subscriptionInvoiceId}>
                  <TableCell>
                    {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                  </TableCell>
                  <TableCell>{formatCurrency(inv.amount)}</TableCell>
                  <TableCell>{formatDate(inv.dueDate)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={InvoiceStatusLabels[inv.status] || inv.status}
                      color={InvoiceStatusColors[inv.status] || "default"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("boSettings:noInvoicesYet")}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// Store owner's side of "cash collected on our behalf by a Prime delivery company" - only
// relevant for owners with at least one Prime store (see StorePayoutsController).
function PayoutsSection() {
  const { t } = useTranslation(["boSettings", "boShared"]);
  const [balances, setBalances] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyPayoutId, setBusyPayoutId] = useState(null);
  const [requestingCompanyId, setRequestingCompanyId] = useState(null);

  function load() {
    setLoading(true);
    setError("");
    Promise.all([getPendingDeliveryBalances(), getMyStorePayouts()])
      .then(([balancesData, payoutsData]) => {
        setBalances(balancesData);
        setPayouts(payoutsData);
      })
      .catch(() => setError(t("boSettings:loadPayoutsError")))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  async function handleRequest(balance) {
    setRequestingCompanyId(balance.deliveryCompanyUserId);
    setError("");
    try {
      await requestStorePayout(balance.deliveryCompanyUserId);
      setNotice(t("boSettings:payoutRequested", { name: balance.deliveryCompanyName }));
      load();
    } catch (err) {
      setError(err.response?.data || t("boSettings:requestPayoutError"));
    } finally {
      setRequestingCompanyId(null);
    }
  }

  async function handleConfirm(payout) {
    setBusyPayoutId(payout.storePayoutId);
    setError("");
    try {
      await confirmStorePayoutReceived(payout.storePayoutId);
      setNotice(
        t("boSettings:payoutConfirmed", {
          amount: formatCurrency(payout.amount),
          name: payout.deliveryCompanyName,
        })
      );
      load();
    } catch {
      setError(t("boSettings:confirmPayoutError"));
    } finally {
      setBusyPayoutId(null);
    }
  }

  const awaiting = payouts.filter((p) => p.status === PAYOUT_STATUS_SENT);
  const history = payouts.filter((p) => p.status === PAYOUT_STATUS_RECEIVED);

  return (
    <SectionCard title={t("boSettings:payoutsTitle")} subtitle={t("boSettings:payoutsSubtitle")}>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : t("boShared:somethingWentWrong")}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rounded" height={100} />
      ) : (
        <Stack spacing={1.5}>
          <SectionLabel>{t("boSettings:heldByDeliveryCompanies")}</SectionLabel>
          {balances.length === 0 ? (
            <EmptyRow text={t("boSettings:noneHeld")} />
          ) : (
            balances.map((balance) => (
              <Card variant="outlined" key={balance.deliveryCompanyUserId}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: "action.hover", color: "text.secondary" }}>
                        <LocalShippingRoundedIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="subtitle2" noWrap>
                        {balance.deliveryCompanyName}
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {formatCurrency((balance.pendingAmount || 0) + (balance.requestedAmount || 0))}
                    </Typography>
                  </Stack>

                  {balance.hasOpenRequest && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                      {balance.openRequests.map((req) => (
                        <Stack
                          key={req.storePayoutRequestId}
                          direction="row"
                          justifyContent="space-between"
                          sx={{ py: 0.5 }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {t("boSettings:orderRequestedCount", { count: req.orderCount })} ·{" "}
                            {formatDate(req.requestedAt)}
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {formatCurrency(req.amount)}
                          </Typography>
                        </Stack>
                      ))}
                    </Box>
                  )}

                  {balance.pendingAmount > 0 && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        {t("boSettings:pendingFrom", {
                          amount: formatCurrency(balance.pendingAmount),
                          count: balance.pendingOrderCount,
                        })}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                        disabled={requestingCompanyId === balance.deliveryCompanyUserId}
                        onClick={() => handleRequest(balance)}
                      >
                        {requestingCompanyId === balance.deliveryCompanyUserId
                          ? t("boSettings:requesting")
                          : t("boSettings:requestPayout")}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            ))
          )}

          <SectionLabel>{t("boSettings:awaitingConfirmation")}</SectionLabel>
          {awaiting.length === 0 ? (
            <EmptyRow text={t("boSettings:nothingAwaiting")} />
          ) : (
            awaiting.map((payout) => (
              <Card variant="outlined" key={payout.storePayoutId}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">{payout.deliveryCompanyName}</Typography>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {formatCurrency(payout.amount)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {t("boSettings:sentOrders", { count: payout.orderCount })} ·{" "}
                    {t("boSettings:sentAt", { date: formatDate(payout.sentAt) })}
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CheckCircleRoundedIcon fontSize="small" />}
                      sx={{ mt: 1 }}
                      disabled={busyPayoutId === payout.storePayoutId}
                      onClick={() => handleConfirm(payout)}
                    >
                      {busyPayoutId === payout.storePayoutId ? t("boSettings:confirming") : t("boSettings:confirmReceived")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}

          {history.length > 0 && (
            <>
              <SectionLabel>{t("boSettings:history")}</SectionLabel>
              <Table size="small">
                <TableBody>
                  {history.map((payout) => (
                    <TableRow key={payout.storePayoutId}>
                      <TableCell>{payout.deliveryCompanyName}</TableCell>
                      <TableCell>{formatCurrency(payout.amount)}</TableCell>
                      <TableCell align="right">{formatDate(payout.receivedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Stack>
      )}
    </SectionCard>
  );
}

// Store owner's side of what they owe the platform directly (Standard stores, which handle
// their own delivery, always pay this way - see StoreFeesController).
function CommissionSection() {
  const { t } = useTranslation(["boSettings", "boShared"]);
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState(null);

  function load() {
    setLoading(true);
    setError("");
    Promise.all([getStoreCommissionSummary(), getStoreFeeRequests()])
      .then(([summaryData, requestsData]) => {
        setSummary(summaryData);
        setRequests(requestsData);
      })
      .catch(() => setError(t("boSettings:loadCommissionError")))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  async function handleConfirmSent(item) {
    setBusyId(item.commissionSettlementId);
    setError("");
    try {
      await confirmStoreFeeSent(item.commissionSettlementId);
      setNotice(t("boSettings:markedAsSent", { amount: formatCurrency(item.amount) }));
      load();
    } catch {
      setError(t("boSettings:markSentError"));
    } finally {
      setBusyId(null);
    }
  }

  const awaiting = requests.filter((r) => r.status === SETTLEMENT_STATUS_GENERATED);
  const history = requests.filter((r) => r.status === SETTLEMENT_STATUS_PAID);

  return (
    <SectionCard title={t("boSettings:commissionTitle")} subtitle={t("boSettings:commissionSubtitle")}>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rounded" height={140} />
      ) : (
        <Stack spacing={1.5}>
          {summary && (
            <Grid container spacing={1.5}>
              <StatTile label={t("boSettings:today")} value={formatCurrency(summary.todayAmount)} />
              <StatTile label={t("boSettings:thisWeek")} value={formatCurrency(summary.weekAmount)} />
              <StatTile label={t("boSettings:thisMonth")} value={formatCurrency(summary.monthAmount)} />
              <StatTile label={t("boSettings:allTime")} value={formatCurrency(summary.allTimeAmount)} />
            </Grid>
          )}

          <SectionLabel>{t("boSettings:awaitingPayment")}</SectionLabel>
          {awaiting.length === 0 ? (
            <EmptyRow text={t("boSettings:nothingRequested")} />
          ) : (
            awaiting.map((item) => (
              <Card variant="outlined" key={item.commissionSettlementId}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">{item.storeName}</Typography>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {formatCurrency(item.amount)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {t("boShared:orderCount", { count: item.orderCount })} · {formatDate(item.periodStart)} –{" "}
                    {formatDate(item.periodEnd)}
                  </Typography>
                  <Box>
                    {item.storeConfirmedSentAt ? (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1, color: "success.main" }}>
                        <CheckCircleRoundedIcon fontSize="small" />
                        <Typography variant="caption">
                          {t("boSettings:sentOn", { date: formatDate(item.storeConfirmedSentAt) })}
                        </Typography>
                      </Stack>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        sx={{ mt: 1 }}
                        disabled={busyId === item.commissionSettlementId}
                        onClick={() => handleConfirmSent(item)}
                      >
                        {busyId === item.commissionSettlementId ? t("boSettings:marking") : t("boSettings:markAsSent")}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))
          )}

          {history.length > 0 && (
            <>
              <SectionLabel>{t("boSettings:history")}</SectionLabel>
              <Table size="small">
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.commissionSettlementId}>
                      <TableCell>{item.storeName}</TableCell>
                      <TableCell>{formatCurrency(item.amount)}</TableCell>
                      <TableCell align="right">{formatDate(item.paidAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Stack>
      )}
    </SectionCard>
  );
}

function StatTile({ label, value }) {
  return (
    <Grid size={{ xs: 6, sm: 3 }}>
      <Box
        sx={{
          p: 1.75,
          borderRadius: 2.5,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
          border: "1px solid",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}
        >
          {label}
        </Typography>
        <Typography variant="h6" fontWeight={800} sx={{ mt: 0.25 }}>
          {value}
        </Typography>
      </Box>
    </Grid>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary" }}>
      {children}
    </Typography>
  );
}

function EmptyRow({ text }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  );
}
